import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ChevronLeft, 
  Edit, 
  UserX, 
  FileText, 
  Clock, 
  Briefcase,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  PiggyBank,
  Calculator,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { DataTable, Column } from '@/components/common/DataTable';
import { 
  EmploymentStatus,
  EMPLOYMENT_STATUSES,
  LEAVE_TYPES,
  LeaveApplication,
} from '@/types';
import { useEmployee, useTerminateEmployee } from '@/hooks/useEmployees';
import { useLeaveApplications } from '@/hooks/useLeave';
import { formatCurrency, formatDate, formatPhone } from '@/lib/utils';
import { cn } from '@/lib/utils';

const getStatusBadge = (status: EmploymentStatus) => {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'> = {
    active: 'success',
    on_leave: 'warning',
    terminated: 'secondary',
  };
  const config = EMPLOYMENT_STATUSES.find((s) => s.value === status);
  return <Badge variant={variants[status] || 'default'}>{config?.label || status}</Badge>;
};

export function EmployeeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('details');

  const { data: employee, isLoading } = useEmployee(id!);
  const { data: leaveApplicationsData } = useLeaveApplications({ employeeId: id });
  const terminateEmployee = useTerminateEmployee();

  const leaveApplications = leaveApplicationsData?.data || [];

  const handleTerminate = async () => {
    if (!confirm('Are you sure you want to terminate this employee? This action cannot be undone.')) return;
    
    try {
      await terminateEmployee.mutateAsync({ 
        id: id!, 
        endDate: new Date().toISOString().split('T')[0] 
      });
      toast({
        title: 'Success',
        description: 'Employee terminated successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to terminate employee',
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Employee not found</p>
        <Button onClick={() => navigate('/payroll/employees')} className="mt-4">
          Back to Employees
        </Button>
      </div>
    );
  }

  const leaveColumns: Column<LeaveApplication>[] = [
    {
      key: 'leaveType',
      header: 'Type',
      cell: (row) => LEAVE_TYPES.find(t => t.value === row.leaveType)?.label || row.leaveType,
    },
    {
      key: 'startDate',
      header: 'Start Date',
      cell: (row) => formatDate(row.startDate),
    },
    {
      key: 'endDate',
      header: 'End Date',
      cell: (row) => formatDate(row.endDate),
    },
    {
      key: 'totalHours',
      header: 'Hours',
      cell: (row) => `${row.totalHours} hrs`,
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => <Badge variant={row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'destructive' : 'warning'}>{row.status}</Badge>,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight">
                {employee.firstName} {employee.lastName}
              </h1>
              {getStatusBadge(employee.employmentStatus)}
            </div>
            <p className="text-sm text-muted-foreground">
              {employee.code} • {employee.jobTitle}
              {employee.department && ` • ${employee.department}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <Link to={`/payroll/employees/${id}?edit=true`}>
              <Edit className="mr-2 h-4 w-4" />
              Edit
            </Link>
          </Button>
          {employee.employmentStatus === 'active' && (
            <Button variant="destructive" onClick={handleTerminate}>
              <UserX className="mr-2 h-4 w-4" />
              Terminate
            </Button>
          )}
        </div>
      </div>

      {/* Employee Profile Card */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Email</p>
                <p className="font-medium">{employee.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Phone className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Phone</p>
                <p className="font-medium">{formatPhone(employee.mobile || employee.phone) || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Employment Type</p>
                <p className="font-medium capitalize">{employee.employmentType.replace('_', ' ')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Start Date</p>
                <p className="font-medium">{formatDate(employee.startDate)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="pay">Pay History</TabsTrigger>
          <TabsTrigger value="leave">Leave</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Personal Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Personal Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Full Name</p>
                    <p className="font-medium">{employee.firstName} {employee.lastName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Date of Birth</p>
                    <p className="font-medium">{employee.dateOfBirth ? formatDate(employee.dateOfBirth) : 'Not provided'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Address</p>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
                    <p className="font-medium">
                      {employee.address ? (
                        <>
                          {employee.address.streetAddress1}
                          {employee.address.streetAddress2 && <>, {employee.address.streetAddress2}</>}
                          <br />
                          {employee.address.suburb}, {employee.address.state} {employee.address.postcode}
                          <br />
                          {employee.address.country}
                        </>
                      ) : (
                        'Not provided'
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Employment Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Employment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Job Title</p>
                    <p className="font-medium">{employee.jobTitle}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Department</p>
                    <p className="font-medium">{employee.department || 'N/A'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Start Date</p>
                    <p className="font-medium">{formatDate(employee.startDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">End Date</p>
                    <p className="font-medium">{employee.endDate ? formatDate(employee.endDate) : 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pay Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calculator className="h-5 w-5" />
                  Pay Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Pay Type</p>
                    <p className="font-medium capitalize">{employee.payType}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Pay Frequency</p>
                    <p className="font-medium capitalize">{employee.payFrequency}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Pay Rate</p>
                  <p className="font-medium text-lg">
                    {formatCurrency(employee.payRate)}
                    <span className="text-sm text-muted-foreground ml-1">
                      /{employee.payType === 'hourly' ? 'hour' : 'year'}
                    </span>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Tax & Super */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PiggyBank className="h-5 w-5" />
                  Tax & Superannuation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tax File Number</p>
                  <p className="font-medium">{employee.tfn || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Tax Scale</p>
                  <p className="font-medium capitalize">{employee.taxScale.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Super Contribution Rate</p>
                  <p className="font-medium">{employee.superContributionRate}%</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Member Number</p>
                  <p className="font-medium">{employee.superMemberNumber || 'N/A'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Bank Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Bank Account
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">BSB</p>
                    <p className="font-medium">{employee.bankBsb || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Account Number</p>
                    <p className="font-medium">{employee.bankAccountNumber || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Account Name</p>
                  <p className="font-medium">{employee.bankAccountName || 'N/A'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Leave Balances */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Leave Balances
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {employee.leaveBalances?.map((balance) => (
                    <div key={balance.type} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div>
                        <p className="font-medium">
                          {LEAVE_TYPES.find(t => t.value === balance.type)?.label || balance.type}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Entitlement: {balance.entitlement} {balance.unit}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={cn(
                          "text-lg font-bold",
                          balance.balance < 10 && "text-warning",
                          balance.balance <= 0 && "text-destructive"
                        )}>
                          {balance.balance} {balance.unit}
                        </p>
                        <p className="text-sm text-muted-foreground">remaining</p>
                      </div>
                    </div>
                  ))}
                  {(!employee.leaveBalances || employee.leaveBalances.length === 0) && (
                    <p className="text-muted-foreground text-center py-4">No leave balances set up</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="pay" className="space-y-6">
          {/* YTD Totals */}
          {employee.ytdTotals && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">YTD Gross</p>
                  <p className="text-2xl font-bold">{formatCurrency(employee.ytdTotals.gross)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">YTD Tax</p>
                  <p className="text-2xl font-bold">{formatCurrency(employee.ytdTotals.tax)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">YTD Super</p>
                  <p className="text-2xl font-bold">{formatCurrency(employee.ytdTotals.super)}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground">YTD Net</p>
                  <p className="text-2xl font-bold">{formatCurrency(employee.ytdTotals.net)}</p>
                </CardContent>
              </Card>
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Pay Run History</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center py-8">
                Pay run history will be displayed here
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leave" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Leave Applications</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                data={leaveApplications}
                columns={leaveColumns}
                keyExtractor={(row) => row.id}
                emptyMessage="No leave applications found"
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Employee Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-center py-8">
                Employee documents will be displayed here
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
